import { Formik, Form } from 'formik';

import * as Yup from 'yup';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { Button, Grid, Stack, Typography, Box } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';
import { ContDePlata_Logo } from '../../Data/imageURL';
import Copyright from '../../Apm/Other/Copyright';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import MetaDecorator from '../../Apm/Other/MetaDecorator';
import Login from '../../Data/MetaData/Login.json';
import useLogin from '../../../hooks/use-login';
import AuthShell from '../../Public/AuthShell';

const initialValues = {
  email: '',
  password: '',
};

const validationSchema = Yup.object({
  email: Yup.string()
    .email(() => i18n.t('yup.invalid_email'))
    .required(() => i18n.t('yup.required')),

  password: Yup.string()
    .required(() => i18n.t('yup.required'))
    .min(8, () => i18n.t('yup.password_is_too_short'))
    .max(20, () => i18n.t('yup.max_20_characters')),
});

export default function LogIn() {
  const { t } = useTranslation();

  const { isLoading, onSubmit } = useLogin();

  const handleSubmit = async (values) => {
    return onSubmit({
      email: values.email.trim().toLowerCase(),

      password: values.password.trim(),
    });
  };

  return (
    <>
      <MetaDecorator
        description={Login.pageDescriptionRo}
        title={Login.pageTitle}
        imageUrl={ContDePlata_Logo}
        imageAlt={Login.pageDescriptionRo}
        rel="/apps/login"
      />

      <AuthShell mode="login">
        <Button component={NavLink} to="/" color="primary" size="small" variant="text">
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Box
              sx={{
                alignItems: 'flex-end',
              }}
            >
              <KeyboardBackspaceIcon />
            </Box>

            <Typography variant="button">{t('button.back_to_main_page')}</Typography>
          </Stack>
        </Button>

        <Typography
          align="center"
          variant="h5"
          sx={{
            mt: 10,
            mb: 2,
          }}
        >
          {t('general.login')}
        </Typography>

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          <Form>
            <Grid container item spacing={2}>
              <Grid item xs={12}>
                <TextFieldNormal
                  variant="outlined"
                  required
                  fullWidth
                  id="email"
                  name="email"
                  label={t('general.email_address')}
                  autoComplete="email"
                />
              </Grid>

              <Grid item xs={12}>
                <TextFieldNormal
                  variant="outlined"
                  required
                  fullWidth
                  id="password"
                  name="password"
                  label={t('general.password')}
                  type="password"
                  autoComplete="current-password"
                />
              </Grid>

              <Grid item xs={12}>
                <ButtonLoading
                  type="submit"
                  fullWidth
                  loading={isLoading}
                  disabled={isLoading}
                  variant="contained"
                  color="primary"
                  sx={{
                    mt: 3,
                    mr: 0,
                  }}
                >
                  {t('button.login')}
                </ButtonLoading>
              </Grid>

              <Grid container item sx={{ mt: 2.5 }} spacing={1}>
                <Grid item xs={12}>
                  <Button component={NavLink} to="/apps/passreset" color="primary" size="small" variant="text">
                    <Typography variant="button">{t('button.forgot_password')}</Typography>
                  </Button>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button component={NavLink} to="/apps/signup" color="primary" size="small" variant="text">
                    <Typography variant="button">{t('button.do_not_have_account')}</Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Formik>

        <Box
          sx={{
            mt: 6,
            mb: 5,
          }}
        >
          <Copyright />
        </Box>
      </AuthShell>
    </>
  );
}
